/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    ServiceEndpoint,
    ServiceEndpointSearchRequest,
} from 'client-code/sls-service-endpoints';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { ISlsServiceEndpointsSearchServiceRepository } from '../interfaces/ISlsServiceEndpointsSearchServiceRepository';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { APPLICATION_FEATURE_AREA, TIVO_SERVICES } from '../../config';
import { Storage } from '../storage/Storage';

export class SlsServiceEndpointsSearchServiceRepositoryImpl
    implements ISlsServiceEndpointsSearchServiceRepository
{
    TAG: string = 'SlsServiceEndpointSearchServiceRepositoryImpl';

    private mSlsEndpointsApi: DefaultApi;

    private mSlsServiceEndpoints: { [key: string]: string } = {};

    constructor(basePath: string) {
        const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;

        const configurationParameters: ConfigurationParameters = {
            basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };

        const configuration = new Configuration(configurationParameters);
        this.mSlsEndpointsApi = new DefaultApi(configuration);
    }

    private async getSlsServiceEndpoints(): Promise<{ [key: string]: string }> {
        try {
            const requestParameters: ServiceEndpointSearchRequest = {
                applicationName: DeviceModel.getAppName(),
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
            };
            const slsServiceEndpointSearchResponse =
                await this.mSlsEndpointsApi.serviceEndpointSearch(requestParameters);

            const { serviceEndpoints } = slsServiceEndpointSearchResponse.data;
            this.initializeSlsEndpoints(serviceEndpoints);
            Storage.getInstance().setItem(
                Storage.Keys.SLS_SERVICE_ENDPOINTS,
                this.mSlsServiceEndpoints,
            );

            return this.mSlsServiceEndpoints;
        } catch (error) {
            Log.e(this.TAG, 'getSlsServiceEndpoints failed');
            Log.d(this.TAG, 'getSlsServiceEndpoints', error);
            const slsEndpoints = Storage.getInstance().getItem(Storage.Keys.SLS_SERVICE_ENDPOINTS);
            if (slsEndpoints) {
                Log.i(this.TAG, 'getSlsServiceEndpoints returning service endpoints from cache');
                return slsEndpoints;
            }
            throw error;
        }
    }

    // function to filter out current service baseUrls
    private initializeSlsEndpoints(slsServiceEndpoints: ServiceEndpoint[] | undefined): void {
        const serviceNames = Object.values(TIVO_SERVICES);

        slsServiceEndpoints?.forEach((slsEndpoint) => {
            if (serviceNames.includes(slsEndpoint.service)) {
                this.mSlsServiceEndpoints[slsEndpoint.service] =
                    this.createServiceEndpoint(slsEndpoint);
            }
        });
    }

    private createServiceEndpoint = (slsEndpoint: ServiceEndpoint): string => {
        if (slsEndpoint.port === 443) {
            return `https://${slsEndpoint.hostname}`;
        }
        if (slsEndpoint.port !== 80) {
            return `http://${slsEndpoint.hostname}:${slsEndpoint.port}`;
        }
        return `http://${slsEndpoint.hostname}`;
    };

    async fetchSlsServiceEndpoints(): Promise<{ [key: string]: string }> {
        try {
            return await this.getSlsServiceEndpoints();
        } catch (error) {
            Log.e(this.TAG, 'fetchSlsServiceEndpoints', error);
            throw error;
        }
    }
}
